/** @format */

/**
 * Shared row hover-action bar. Fully CONFIG-DRIVEN — each app passes its own list
 * of actions (icon + label + handler), so the same icon can do different things
 * per app/row (e.g. CRM's "call" vs projects' "open"). Rendered consistently:
 * small ghost icon buttons revealed on row hover, with the system-standard small
 * radius + icon size. Use inside a typed column's `typeOptions.actions(row)`.
 */

import type { MouseEvent } from 'react';
import type { LucideIcon } from 'lucide-react';
import { Button, cn, Tooltip, TooltipContent, TooltipTrigger } from '@clarix/ui';
import { HOVER_RING_TIGHT } from '../../Badge';

/** Shape/size of a row-action icon button — everything except its ink. */
const ROW_ACTION_SHAPE = 'h-5 w-5 rounded-sm hover:bg-transparent [&_svg]:!size-3.5';

/**
 * The ONE look for a table row-action icon button — 20px ghost square, ink that
 * resolves to full foreground on hover, tight focus ring, 14px glyph.
 *
 * Ink is `foreground/70`, NOT `muted-foreground`: the muted token is `--ink-3`
 * (#9A9CA5), the scale's faintest usable step, which left these glyphs washed
 * out. 70% of `--foreground` lands around #606163 — clearly present, still
 * plainly secondary to the row's own text. Destructive keeps the lighter muted
 * ink deliberately (see below).
 *
 * Exported because not every action cluster can use {@link RowActions} itself:
 * some hold form controls that must stay visible (an inline-edit Save/Cancel
 * pair, a per-row Save), and `RowActions` is hover-gated. Those clusters stay
 * hand-rolled but MUST wear this class so they don't drift back to their old
 * assorted `h-6`/`h-7` sizes. Compose with `cn()` and put any tone override
 * AFTER it — `twMerge` lets the later colour win.
 */
export const ROW_ACTION_BUTTON = cn(
  'text-foreground/70 hover:text-foreground',
  ROW_ACTION_SHAPE,
  HOVER_RING_TIGHT,
);

/**
 * {@link ROW_ACTION_BUTTON} for destructive actions — red ink on hover only.
 *
 * Keeps the ORIGINAL `muted-foreground` ink rather than inheriting the darker
 * `foreground/70` above: a delete should stay the quietest thing in the cluster
 * until you reach for it, so it is deliberately NOT built on ROW_ACTION_BUTTON.
 */
export const ROW_ACTION_BUTTON_DESTRUCTIVE = cn(
  'text-muted-foreground',
  ROW_ACTION_SHAPE,
  HOVER_RING_TIGHT,
  'hover:!text-destructive',
);

export interface RowAction {
  icon: LucideIcon;
  label: string;
  /**
   * Receives the originating click so an action can honour modifier keys
   * (e.g. ⌘/Ctrl-click on "Open" → new tab). Handlers that don't care simply
   * ignore the argument.
   */
  onClick: (e: MouseEvent) => void;
  /**
   * Tooltip text when it should say more than the action's name — e.g.
   * "Open · ⌘/Ctrl-click for a new tab". `label` still drives `aria-label`,
   * so assistive tech keeps hearing the short name.
   */
  tooltip?: string;
  /** Destructive actions tint red on hover (e.g. Delete). */
  tone?: 'default' | 'destructive';
  /** Hide this action for the current row (per-row dynamic visibility). */
  hidden?: boolean;
  /** Disable without removing (e.g. no permission). */
  disabled?: boolean;
}

export function RowActions({ items, className }: { items: RowAction[]; className?: string }) {
  const visible = items.filter((a) => !a.hidden);
  if (!visible.length) return null;
  return (
    // Hover-gated by default (overlay mode), and shown while focus is inside the row so
    // keyboard users can Tab to the actions. Inside the STICKY actions column the parent
    // cell force-shows this via `[&_[data-row-actions]]:!flex`.
    <div
      data-row-actions=""
      className={cn(
        'hidden items-center gap-0.5 group-focus-within/row:flex group-hover/row:flex',
        className,
      )}
    >
      {visible.map((a) => (
        // The shared Tooltip, NOT the native `title` attribute: `title` renders the
        // browser's own OS tooltip — different font, colour, corner and delay from
        // every other affordance in the product, and it sat right beside the Notes
        // button's styled one. `aria-label` still carries the name for assistive
        // tech. The table mounts its own TooltipProvider, so this works even where
        // the row actions render outside an app shell.
        <Tooltip key={a.label}>
          <TooltipTrigger asChild>
            <Button
              type="button"
              size="icon"
              variant="ghost"
              disabled={a.disabled}
              aria-label={a.label}
              className={
                a.tone === 'destructive' ? ROW_ACTION_BUTTON_DESTRUCTIVE : ROW_ACTION_BUTTON
              }
              onClick={(e) => {
                e.stopPropagation();
                a.onClick(e);
              }}
            >
              <a.icon />
            </Button>
          </TooltipTrigger>
          <TooltipContent>{a.tooltip ?? a.label}</TooltipContent>
        </Tooltip>
      ))}
    </div>
  );
}
